import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {StandaloneComponent} from '../standalone.component';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthenticationInfoService} from '../authentication-info.service';
import {ParamsService} from '../params.service';
import {AppConstants} from '../AppConstants';
import {DomSanitizer} from '@angular/platform-browser';
import { getApiBasePath } from '../../api/real-api/api-base-path';
import {PlatformLocation} from '@angular/common';
import {Logger} from '../../util/logger';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';

const _ = (s) => s;

interface LogoutComponentParams {
  next?: string;
  initiator: string;
  initiatorType?: string;
}
interface ConnectedApplication {
  applicationId: string;
  applicationName?: string;
  protocol?: string;
}
interface LogoutResponse {
  applicationId: string;
  applicationType: string;
  type: string;
  status: string;
}

@Component({
  selector: 'app-logout',
  templateUrl: './logout.component.html',
  styleUrls: ['./logout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LogoutComponent extends StandaloneComponent implements OnInit, OnDestroy {

  consumers: Array<ConnectedApplication> = [];
  providers: Array<ConnectedApplication> = [];
  private trustedOrigins: Array<string>;
  private destroyed = false;
  /**
   * To avoid flashing content, we store the show time and once completed we check if the
   * duration was longer than the treshold, and if so, we wait for the remaining duration of
   * the minimumDisplayTime
   */
  private startTime: number;
  private timeTreshold = 50;
  private minimumDisplayTime = 1000;
  private maximumResponseWaitTime = 5000;
  @HostListener('window:message', ['$event'])
  onMessage(e: MessageEvent)  {
    if (e && this.trustedOrigins.indexOf(e.origin) >= 0 && e.data && e.data.type === 'logout-response') {
      try {
        const obj = e.data as LogoutResponse;
        if (obj.status === 'completed') {
          if (obj.applicationType.toLowerCase().indexOf('consumer') >= 0) {
            this.consumerLoggedOut(obj.applicationId);
          } else if (obj.applicationType.toLowerCase().indexOf('provider') >= 0) {
            this.providerLoggedOut(obj.applicationId);
          }
        }
      } catch (e) {}
    }
  }
  constructor(
    route: ActivatedRoute,
    router: Router,
    hostElement: ElementRef,
    private translate: TranslateService,
    private authnInfoService: AuthenticationInfoService,
    private paramsService: ParamsService,
    private sanitizer: DomSanitizer,
    private logger: Logger,
    private platformLocation: PlatformLocation) {
    super(route, router, hostElement);
    const temp = getApiBasePath(this.platformLocation).split('/');
    // assign api server and self as trusted origins, should be equal in all real depoloyments, but may be different with test setups
    this.trustedOrigins = [
      temp[0] + '//' + temp[2],
      this.platformLocation.protocol + '//' + this.platformLocation.hostname +
        (this.platformLocation.port ? ':' + this.platformLocation.port : ''),
    ];

  }
  consumerLoggedOut(id: string) {
    if (id) {
      this.consumers = this.consumers.filter(itm => itm.applicationId !== id);
      if (this.consumers.length === 0 && this.providers.length === 0) {
        this.logoutCompleted();
      }
    }
  }
  providerLoggedOut(id: string) {
    if (id) {
      this.providers = this.providers.filter(itm => itm.applicationId !== id);
      if (this.providers.length === 0) {
        this.logoutCompleted();
      }
    }
  }
  getConsumerLogoutUrl(itm: ConnectedApplication): any {
    setTimeout(() => {
      this.consumerLoggedOut(itm.applicationId);
    }, this.maximumResponseWaitTime);
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      getApiBasePath(this.platformLocation) + '/sessions/clients/' + itm.applicationId + '/signOut');
  }
  getProviderLogoutUrl(itm: ConnectedApplication): any {
    setTimeout(() => {
      this.providerLoggedOut(itm.applicationId);
    }, this.maximumResponseWaitTime);
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      getApiBasePath(this.platformLocation) + '/sessions/externalIdentityProviders/' + itm.applicationId + '/signOut');
  }
  ngOnInit() {
    this.startTime = new Date().getTime();
    this.authnInfoService.startTask('logout');
    this.readParams().then((result) => {
      if (!this.authnInfoService.sessionInfo.value.hasSession) {
        this.authnInfoService.completeTask('logout');
        if (result && result.next) {
          window.location.href = result.next;
        } else {
          this.router.navigate([AppConstants.ROUTE_LOGIN], { replaceUrl: true });
        }
        return;
      }
      this.authnInfoService.endSession(false, false).subscribe((next) => {
        this.logger.debug('logout success');
        this.logoutCompleted();
      }, (err) => {
        this.logger.debug('logout err');
        if (err && err.error && err.error.sessionExists === false) {

          if (result && result.next) {
            window.location.href = result.next;
          } else {
            this.logger.debug('logout done');
            this.logoutCompleted();
          }
        } else if (err && err.error && err.error.error === 'sso_sessions_exist') {
          if (err.error.consumers && err.error.consumers.length > 0) {
            this.consumers = err.error.consumers as Array<ConnectedApplication>;
            if ((result.initiatorType && result.initiatorType.toLowerCase().indexOf('consumer') >= 0) ||
                 !result.initiatorType) {
              this.consumers = this.consumers.filter(
                (val) => val.applicationId !== result.initiator
              );
            }
          } else {
            this.consumers = [];
          }
          if (err.error.providers && err.error.providers.length > 0) {
            this.providers = err.error.providers as Array<ConnectedApplication>;
            if ((result.initiatorType && result.initiatorType.toLowerCase().indexOf('provider') >= 0) ||
              !result.initiatorType) {
              this.providers = this.providers.filter(
                (val) => val.applicationId !== result.initiator
              );
            }
          } else {
            this.providers = [];
          }
        }
        this.logger.debug(err);
      });
    });

  }
  logoutCompleted() {
    this.logger.debug('logout completed');
    const handler = () => {
      if (!this.destroyed) {
        this.authnInfoService.completeTask('logout');
        this.authnInfoService.fetchAuthenticationInfo();
      }
    };
    const callback = () => {
      const t = new Date().getTime();
      const delta = t - this.startTime;
      if (delta > this.timeTreshold) {
        let wait = this.minimumDisplayTime - delta;
        if (wait < 1) {
          wait = 1;
        }
        setTimeout(handler, wait);
      } else {
        handler();
      }
    };
    this.authnInfoService.endSession(true, true).subscribe(callback, callback);
  }
  ngOnDestroy() {
    this.destroyed = true;
    if (this.authnInfoService.sessionInfo.value.tasksInProgress.indexOf('logout') >= 0) {
      this.authnInfoService.cancelTask('logout');
    }
  }
  private readParams(anim?: boolean): Promise<LogoutComponentParams> {
    return new Promise<LogoutComponentParams>((resolve) => {
      const nextPromise = this.paramsService.getParam(AppConstants.QP_NEXT);
      const initiatorPromise = this.paramsService.getParam(AppConstants.QP_LOGOUT_INITIATOR);
      const initiatorTypePromise = this.paramsService.getParam(AppConstants.QP_LOGOUT_INITIATOR_TYPE);
      Promise.all([nextPromise, initiatorPromise, initiatorTypePromise]).then((values) => {

        const result: LogoutComponentParams = {
          next: values[0],
          initiator: values[1],
          initiatorType: values[2],
        };

        resolve(result);
      });
    });
  }
  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.translate.get('logout.window-title');
  }
}
